import React, { useState, useEffect, useCallback, useRef } from 'react';
// import axios from 'axios'; // No longer needed
import api from '../../api/axios'; // Use our new authenticated api instance
import supabase from '../../api/supabase';
import { toast } from 'react-hot-toast';
import {
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ExclamationCircleIcon
} from "@heroicons/react/24/solid";
import Modal from '../../components/Modal';
import ConstructionTimeline from '../../components/ui/ConstructionTimeline';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import './DashboardHome.css'; // Add this import for custom styles

// Helper function to format numbers
const formatNumber = (num) => num ? num.toLocaleString('en-IN') : '0';

// Utility to format numbers in Indian system (e.g., 1 crore 30 lakh)
function formatIndianPrice(num) {
  if (!num) return 'N/A';
  num = Number(num);
  if (isNaN(num)) return num;
  if (num >= 10000000) {
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    return `${crore} crore${lakh ? ' ' + lakh + ' lakh' : ''}`;
  } else if (num >= 100000) {
    const lakh = Math.floor(num / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    return `${lakh} lakh${thousand ? ' ' + thousand + ' thousand' : ''}`;
  } else if (num >= 1000) {
    const thousand = Math.floor(num / 1000);
    return `${thousand} thousand`;
  }
  return num.toString();
}

// A single, reusable loading/error component
const DataWrapper = ({ loading, error, loadingText, children }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 min-h-[150px] bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-palamuru-blue mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">{loadingText}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg text-center">
        <p className="font-semibold">⚠️ Error</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return children;
};

const STATUS_LABELS = {
  all: 'All',
  complete: 'Complete',
  inprogress: 'In Progress',
  delayed: 'Delayed',
  notstarted: 'Not Started',
};

const getPhaseStatus = (phase) => {
  const plannedEndDate = new Date(phase.plannedEnd);
  const today = new Date();
  if ((phase.actualProgressPercent ?? 0) >= 100 || phase.status === "Completed" || phase.status === "Complete") {
    if (phase.actualEnd && new Date(phase.actualEnd) > plannedEndDate) return 'delayed';
    return 'complete';
  } else if (phase.actualProgressPercent > 0) {
    return today > plannedEndDate ? 'delayed' : 'inprogress';
  } else if (today > plannedEndDate) {
    return 'delayed';
  }
  return 'notstarted';
};

// PaymentPieChart component
const PaymentPieChart = ({ paid, pending, outstanding }) => {
  const data = [
    { name: 'Paid', value: paid },
    { name: 'Pending', value: pending },
    { name: 'Outstanding', value: outstanding },
  ].filter(d => d.value > 0);
  const COLORS = ['#22c55e', '#facc15', '#ef4444'];
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => `₹${formatNumber(value)}`} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

// PaymentProgressBar component
const PaymentProgressBar = ({ paid, pending, remaining, total }) => {
  const paidPercent = total ? (paid / total) * 100 : 0;
  const pendingPercent = total ? (pending / total) * 100 : 0;
  const remainingPercent = total ? (remaining / total) * 100 : 0;
  return (
    <div className="w-full h-5 bg-gray-200 rounded-full flex overflow-hidden shadow-inner">
      <div className="h-5 bg-green-400" style={{ width: `${paidPercent}%` }}></div>
      <div className="h-5 bg-yellow-400" style={{ width: `${pendingPercent}%` }}></div>
      <div className="h-5 bg-blue-300" style={{ width: `${remainingPercent}%` }}></div>
    </div>
  );
};

// Custom hook to detect small screens
function useIsSmallScreen() {
  const [isSmall, setIsSmall] = useState(() => window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsSmall(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isSmall;
}

const DashboardHome = () => {
  // State for API data
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [phases, setPhases] = useState([]);
  const [user, setUser] = useState('');
  const [payments, setPayments] = useState([]);

  // Loading states
  const [loading, setLoading] = useState({ stats: true, activities: true, timeline: true });
  const [error, setError] = useState({ stats: null, activities: null, timeline: null });

  // Manual refresh function for testing
  const handleManualRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    // Define fetchPhases here so it can be called
    const fetchPhases = async () => {
      try {
        setLoading(prev => ({ ...prev, timeline: true }));
        const response = await api.get('/construction/phases');
        console.log('📊 Dashboard fetched phases:', response.data);
        const mappedPhases = response.data.map(p => ({
          ...p,
          phase: p.name,
          plannedStart: p.start_date,
          plannedEnd: p.end_date,
          actualProgressPercent: p.progress ?? 0,
          status: p.status
        }));
        console.log('📊 Dashboard mapped phases:', mappedPhases);
        console.log('📊 First phase details:', mappedPhases[0]);
        setPhases(mappedPhases);
      } catch (err) {
        console.error('❌ Dashboard fetchPhases error:', err);
        setError(prev => ({ ...prev, timeline: 'Could not load phases.' }));
      } finally {
        setLoading(prev => ({ ...prev, timeline: false }));
      }
    };
    fetchPhases();
  };

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async (endpoint, dataKey, loadingKey, errorKey) => {
      try {
        setLoading(prev => ({ ...prev, [loadingKey]: true }));
        // Use the 'api' instance and simplified URL
        const response = await api.get(`/dashboard/${endpoint}`);
        
        if (dataKey === 'stats') setStats(response.data);
        else if (dataKey === 'activities') setActivities(response.data);
        else if (dataKey === 'phases') {
          // Map backend fields to frontend expected fields for timeline
          setPhases(
            response.data.map(p => ({
              ...p,
              phase: p.name,
              plannedStart: p.start_date,
              plannedEnd: p.end_date,
              actualProgressPercent: p.progress ?? 0,
              status: p.status
            }))
          );
        }

      } catch (err) {
        console.error(`Failed to fetch ${dataKey}:`, err);
        setError(prev => ({ ...prev, [errorKey]: `Could not load ${dataKey}.` }));
      } finally {
        setLoading(prev => ({ ...prev, [loadingKey]: false }));
      }
    };

    fetchData('stats', 'stats', 'stats', 'stats');
    fetchData('activity', 'activities', 'activities', 'activities');

    // Initial fetch of phases
    handleManualRefresh();

    // Fetch user profile on mount
    const fetchUser = async () => {
      try {
        const res = await api.get('/users/profile');
        setUser(res.data.full_name || res.data.name || res.data.email?.split('@')[0] || 'User');
      } catch (err) {
        setUser('User');
      }
    };
    fetchUser();

    // Fetch payments on mount
    const fetchPayments = async () => {
      try {
        const res = await api.get('/payments');
        setPayments(res.data.data || []);
      } catch (err) {
        setPayments([]);
      }
    };
    fetchPayments();

    // --- Real-time subscription for project updates ---
    const projectSubscription = supabase
      .channel('dashboard-project-changes')
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects' },
        (payload) => {
          console.log('🔄 Dashboard received real-time project update:', payload);
          console.log('🔄 Payload new record:', payload.new);
          console.log('🔄 Payload old record:', payload.old);
          toast.success('Project updated! Dashboard refreshed.');
          fetchData('stats', 'stats', 'stats', 'stats');
          fetchData('activity', 'activities', 'activities', 'activities');
          handleManualRefresh();
        }
      )
      .on('error', (error) => {
        console.error('❌ Real-time subscription error:', error);
      })
      .on('close', () => {
        console.log('📡 Real-time subscription closed');
      })
      .subscribe((status) => {
        console.log('📡 Real-time subscription status:', status);
      });

    console.log('📡 Dashboard real-time subscription established');

    return () => {
      projectSubscription.unsubscribe();
    };
  }, []);

  // Quick Actions modal state
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showQualityModal, setShowQualityModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [visitSuccess, setVisitSuccess] = useState(false);
  const [questionSuccess, setQuestionSuccess] = useState(false);

  // Form state (minimal for demo)
  const [visitDate, setVisitDate] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [questionSubject, setQuestionSubject] = useState('');
  const [questionMessage, setQuestionMessage] = useState('');
  const [questionCategory, setQuestionCategory] = useState('');

  // Modal for dashboard card/progress details
  const [activeModal, setActiveModal] = useState(null); // 'progress' | 'days' | 'milestone' | 'visits' | 'phase-Foundation' | ...

  const [statusFilter, setStatusFilter] = useState('all');

  const isSmallScreen = useIsSmallScreen();

  const statCards = [
    { name: "Overall Progress", value: `${stats?.progress || 0}%`, icon: <ArrowTrendingUpIcon className="h-6 w-6 text-green-500" /> },
    { name: "Phases Completed", value: `${stats?.completedPhases || 0} / ${stats?.totalPhases || 0}`, icon: <CheckCircleIcon className="h-6 w-6 text-blue-500" /> },
    {
      name: "Price",
      value: (
        <>
          <span className="responsive-amount">
            {isSmallScreen ? formatIndianPrice(stats?.soldCost) : `₹${formatNumber(stats?.soldCost)}`}
          </span>
          {isSmallScreen && (
            <span className="text-xs text-gray-500 mt-1 block">₹{formatNumber(stats?.soldCost)}</span>
          )}
        </>
      ),
      icon: <CurrencyRupeeIcon className="h-6 w-6 text-red-500" />
    },
    { name: "Days Left (Est.)", value: stats?.daysLeft || 0, icon: <ClockIcon className="h-6 w-6 text-yellow-500" /> },
  ];

  // Calculate payment stats for the pie chart
  const totalPrice = Number(stats?.soldCost) || 0;
  const paid = payments?.filter(p => (p.status || '').toLowerCase() === 'paid').reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const pending = payments?.filter(p => ['pending', 'due', 'requested', 'awaiting_confirmation'].includes((p.status || '').toLowerCase())).reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const outstanding = Math.max(totalPrice - paid - pending, 0);

  return (
    <div className="p-6 lg:p-8 font-body">
      <h1 className="text-2xl lg:text-3xl font-display-bold text-gray-900 mb-6">
        Welcome Back, {user || '...'}!
      </h1>

      {/* Stats Cards */}
      <div className="mb-8">
        <h2 className="text-xl font-display-semibold text-gray-800 mb-4">Project Snapshot</h2>
        <DataWrapper loading={loading.stats} error={error.stats} loadingText="Loading stats...">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((item) => (
              <div key={item.name} className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100 flex items-center gap-5">
                <div className="bg-gray-100 p-3 rounded-full">{item.icon}</div>
                <div style={{ minWidth: 0, flexShrink: 1 }}>
                  <p className="text-sm text-gray-500">{item.name}</p>
                  <p className="text-2xl font-display-bold text-gray-900">{item.value}</p>
                  {item.name === 'Price' && (
                    <p className="text-xs text-gray-500 mt-1">{formatIndianPrice(stats?.soldCost)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DataWrapper>
      </div>

      {/* Construction Timeline */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-display-semibold text-gray-800">Construction Timeline</h2>
          <button
            onClick={handleManualRefresh}
            className="px-3 py-1 bg-palamuru-blue text-white rounded-lg text-sm hover:bg-palamuru-blue-dark transition-colors"
          >
            Refresh
          </button>
        </div>
        <div className="mb-4 flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700 mr-2">Filter by Status:</span>
          <div className="flex gap-2">
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={
                  (statusFilter === key
                    ? "bg-palamuru-blue text-white shadow font-semibold"
                    : "bg-white text-gray-700 border border-gray-300 hover:bg-blue-50") +
                  " px-4 py-2 rounded-full text-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400"
                }
                style={{ minWidth: 90 }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <DataWrapper loading={loading.timeline} error={error.timeline} loadingText="Loading timeline...">
          <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100">
            {phases.length > 0 ? (
              <ConstructionTimeline
                phases={
                  statusFilter === 'all'
                    ? phases
                    : phases.filter(phase => getPhaseStatus(phase) === statusFilter)
                }
              />
            ) : (
              <p className="text-sm text-gray-500 text-center">No timeline data available.</p>
            )}
          </div>
        </DataWrapper>
      </div>

      {/* Payment Summary - improved UI */}
      <div className="mb-8">
        <h2 className="text-xl font-display-semibold text-gray-800 mb-4">Payment Summary</h2>
        <div className="bg-white p-8 rounded-2xl shadow-soft border border-gray-100 flex flex-col gap-6">
          <PaymentProgressBar paid={paid} pending={pending} remaining={outstanding} total={totalPrice} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
            <div className="flex flex-col items-center bg-blue-50 rounded-xl p-4 shadow-sm">
              <CurrencyRupeeIcon className="h-7 w-7 text-blue-500 mb-1" />
              <span className="text-xs font-medium text-blue-700 mb-1">Total Price</span>
              <span className="text-xl font-bold text-blue-700 responsive-amount">
                {isSmallScreen ? formatIndianPrice(totalPrice) : `₹${formatNumber(totalPrice)}`}
              </span>
              <span className="text-xs text-gray-500 mt-1 block">{formatIndianPrice(totalPrice)}</span>
              {isSmallScreen && (
                <span className="text-xs text-gray-500 mt-1 block">₹{formatNumber(totalPrice)}</span>
              )}
            </div>
            <div className="flex flex-col items-center bg-green-50 rounded-xl p-4 shadow-sm">
              <CheckCircleIcon className="h-7 w-7 text-green-500 mb-1" />
              <span className="text-xs font-medium text-green-700 mb-1">Paid</span>
              <span className="text-xl font-bold text-green-700 responsive-amount">
                {isSmallScreen ? formatIndianPrice(paid) : `₹${formatNumber(paid)}`}
              </span>
              <span className="text-xs text-gray-500 mt-1 block">{formatIndianPrice(paid)}</span>
              {isSmallScreen && (
                <span className="text-xs text-gray-500 mt-1 block">₹{formatNumber(paid)}</span>
              )}
            </div>
            <div className="flex flex-col items-center bg-yellow-50 rounded-xl p-4 shadow-sm">
              <ClockIcon className="h-7 w-7 text-yellow-500 mb-1" />
              <span className="text-xs font-medium text-yellow-700 mb-1">Pending</span>
              <span className="text-xl font-bold text-yellow-700 responsive-amount">
                {isSmallScreen ? formatIndianPrice(pending) : `₹${formatNumber(pending)}`}
              </span>
              <span className="text-xs text-gray-500 mt-1 block">{formatIndianPrice(pending)}</span>
              {isSmallScreen && (
                <span className="text-xs text-gray-500 mt-1 block">₹{formatNumber(pending)}</span>
              )}
            </div>
            <div className="flex flex-col items-center bg-purple-50 rounded-xl p-4 shadow-sm">
              <ExclamationCircleIcon className="h-7 w-7 text-purple-500 mb-1" />
              <span className="text-xs font-medium text-purple-700 mb-1">Remaining</span>
              <span className="text-xl font-bold text-purple-700 responsive-amount">
                {isSmallScreen ? formatIndianPrice(outstanding) : `₹${formatNumber(outstanding)}`}
              </span>
              <span className="text-xs text-gray-500 mt-1 block">{formatIndianPrice(outstanding)}</span>
              {isSmallScreen && (
                <span className="text-xs text-gray-500 mt-1 block">₹{formatNumber(outstanding)}</span>
              )}
            </div>
          </div>
          {payments.length === 0 && (
            <div className="text-xs text-gray-500 text-center mt-4">No payment requests have been raised yet by the admin.</div>
          )}
        </div>
      </div>

      {/* Recent Activity - now full width below timeline */}
      <div className="mb-8">
        <h2 className="text-xl font-display-semibold text-gray-800 mb-4">Recent Activity</h2>
        <DataWrapper loading={loading.activities} error={error.activities} loadingText="Loading activities...">
          <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100 space-y-4">
            {activities.length > 0 ? activities.map((activity, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-1 w-3 h-3 bg-palamuru-blue rounded-full flex-shrink-0"></div>
                <div>
                  <p className="font-semibold text-gray-800">{activity.title}</p>
                  <p className="text-sm text-gray-500">{activity.description}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(activity.date).toLocaleDateString()}</p>
                </div>
              </div>
            )) : <p className="text-sm text-gray-500 text-center">No recent activity.</p>}
          </div>
        </DataWrapper>
      </div>

      {/* Modals */}
      {/* Site Visit Modal */}
      <Modal isOpen={showVisitModal} onClose={() => setShowVisitModal(false)}>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Schedule Site Visit</h2>
          {!visitSuccess ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              setVisitSuccess(true);
              setTimeout(() => setShowVisitModal(false), 2000);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                  <input
                    type="date"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    value={visitNotes}
                    onChange={(e) => setVisitNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-transparent"
                    placeholder="Any specific areas you'd like to focus on..."
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-palamuru-blue text-white py-2 px-4 rounded-lg hover:bg-palamuru-blue-dark transition-colors"
                  >
                    Schedule Visit
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVisitModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="text-green-500 text-4xl mb-4">✅</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Visit Scheduled!</h3>
              <p className="text-gray-600">We'll confirm the details via email shortly.</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Question Modal */}
      <Modal isOpen={showQuestionModal} onClose={() => setShowQuestionModal(false)}>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Ask a Question</h2>
          {!questionSuccess ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              setQuestionSuccess(true);
              setTimeout(() => setShowQuestionModal(false), 2000);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={questionCategory}
                    onChange={(e) => setQuestionCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-transparent"
                    required
                  >
                    <option value="">Select a category</option>
                    <option value="construction">Construction Progress</option>
                    <option value="quality">Quality & Materials</option>
                    <option value="timeline">Timeline & Schedule</option>
                    <option value="payment">Payment & Billing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={questionSubject}
                    onChange={(e) => setQuestionSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-transparent"
                    placeholder="Brief subject line"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={questionMessage}
                    onChange={(e) => setQuestionMessage(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-palamuru-blue focus:border-transparent"
                    placeholder="Describe your question in detail..."
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-palamuru-blue text-white py-2 px-4 rounded-lg hover:bg-palamuru-blue-dark transition-colors"
                  >
                    Send Question
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowQuestionModal(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="text-green-500 text-4xl mb-4">✅</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Question Sent!</h3>
              <p className="text-gray-600">We'll get back to you within 24 hours.</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DashboardHome; 